import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CsvImportBar } from "../../components/CsvImportBar";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import type { CsvImportPreview, TargetListItem } from "../../api/targets";
import { createGroup, fetchGroups } from "../../api/groups";
import {
  looksLikePhone,
  useCreateTarget,
  useDeleteTarget,
  useImportTargets,
  useTargetByPhone,
  useTargets,
  useUpdateTarget,
} from "../../hooks/useTargets";
import { Sheet } from "../../components/Sheet";
import { TargetDrawer } from "./Drawer";
import { TargetsList } from "./List";
import { TargetsToolbar } from "./Toolbar";
import { Button } from "../../components/ui/Button";

// Same hard-coded customer scope Groups/List.tsx + Campaigns/List.tsx use until
// multi-customer auth lands. Keep them in sync.
const CUSTOMER_ID = "00000000-0000-0000-0000-000000000001";

export function TargetsIndex() {
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [ungrouped, setUngrouped] = useState(false);
  const [selected, setSelected] = useState<TargetListItem | null>(null);
  const [preview, setPreview] = useState<CsvImportPreview | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("");
  const [newDepartment, setNewDepartment] = useState("");
  const [groupId, setGroupId] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  // Selection (bulk delete) + delete confirm state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmTarget, setConfirmTarget] = useState<TargetListItem | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);

  function resetCreateForm() {
    setNewName("");
    setNewFirstName("");
    setNewLastName("");
    setNewPhone("");
    setNewEmail("");
    setNewRole("");
    setNewDepartment("");
    setGroupId("");
    setNewGroupName("");
    setCreateError(null);
  }

  const isPhone = looksLikePhone(query);

  const { data: listData } = useTargets({ q: query || undefined, ungrouped: ungrouped || undefined });
  const phoneLookup = useTargetByPhone(query, isPhone && !ungrouped);
  const { preview: previewMutation, commit: commitMutation } = useImportTargets();
  const updateTarget = useUpdateTarget(selected?.target_id ?? "");
  const createTarget = useCreateTarget();
  const deleteTarget = useDeleteTarget();

  const { data: groupsData } = useQuery({ queryKey: ["groups"], queryFn: fetchGroups });
  const groups = groupsData?.items ?? [];

  const createGroupMut = useMutation({
    mutationFn: () => createGroup({ customer_id: CUSTOMER_ID, name: newGroupName.trim() }),
    onSuccess: (record) => {
      void qc.invalidateQueries({ queryKey: ["groups"] });
      setGroupId(record.group_id);
      setNewGroupName("");
    },
    onError: (err) =>
      setCreateError(err instanceof Error ? err.message : "Failed to create group."),
  });

  // Merge phone-GSI hit to front, de-duplicate by target_id
  const items = useMemo<TargetListItem[]>(() => {
    const base = listData?.items ?? [];
    const exact = phoneLookup.data;
    if (!exact) return base;
    const others = base.filter((t) => t.target_id !== exact.target_id);
    return [exact as TargetListItem, ...others];
  }, [listData?.items, phoneLookup.data]);

  const matchedPhone: string | null = phoneLookup.data?.primary_phone_e164 ?? null;

  const creatingNewGroup = groupId === "__new__";
  const canCreate =
    !!newName.trim() &&
    (!!newPhone.trim() || !!newEmail.trim()) &&
    !!groupId &&
    !creatingNewGroup &&
    !createTarget.isPending;

  async function handleImport(file: File) {
    try {
      const p = await previewMutation.mutateAsync(file);
      setPreview(p);
    } catch {
      // previewMutation.error surfaces via the CsvImportBar error prop below
    }
  }

  function handleCommit() {
    if (!preview) return;
    void commitMutation.mutateAsync(preview.preview_token).then(() => setPreview(null));
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds((prev) =>
      prev.size === items.length ? new Set() : new Set(items.map((t) => t.target_id)),
    );
  }

  function confirmSingleDelete() {
    if (!confirmTarget) return;
    const id = confirmTarget.target_id;
    deleteTarget.mutate(id, {
      onSuccess: () => {
        setConfirmTarget(null);
        if (selected?.target_id === id) setSelected(null);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      },
      onError: (err) =>
        alert(err instanceof Error ? err.message : "Failed to delete target."),
    });
  }

  async function confirmBulkDelete() {
    const ids = [...selectedIds];
    const results = await Promise.allSettled(ids.map((id) => deleteTarget.mutateAsync(id)));
    const failed = results.filter((r) => r.status === "rejected").length;
    setConfirmBulk(false);
    setSelectedIds(new Set());
    void qc.invalidateQueries({ queryKey: ["targets"] });
    if (failed > 0) alert(`${failed} of ${ids.length} deletions failed.`);
  }

  const selectedTestCount = useMemo(
    () => items.filter((t) => selectedIds.has(t.target_id) && t.test_only).length,
    [items, selectedIds],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <TargetsToolbar
        count={listData?.total ?? 0}
        query={query}
        onQuery={setQuery}
        onImport={(file) => void handleImport(file)}
        onExport={() => exportCsv(items)}
        onAdd={() => setShowCreate(true)}
        ungrouped={ungrouped}
        onToggleUngrouped={() => setUngrouped((v) => !v)}
        selectedCount={selectedIds.size}
        onDeleteSelected={() => setConfirmBulk(true)}
      />

      {showCreate && (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 16,
            display: "flex",
            gap: 10,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <CreateField label="Full name *" value={newName} onChange={(v) => { setNewName(v); setCreateError(null); }} autoFocus />
          <CreateField label="First name" value={newFirstName} onChange={(v) => { setNewFirstName(v); setCreateError(null); }} />
          <CreateField label="Last name" value={newLastName} onChange={(v) => { setNewLastName(v); setCreateError(null); }} />
          <CreateField label="Phone (E.164)" value={newPhone} mono onChange={(v) => { setNewPhone(v); setCreateError(null); }} />
          <CreateField label="Email" value={newEmail} onChange={(v) => { setNewEmail(v); setCreateError(null); }} />
          <CreateField label="Role" value={newRole} onChange={(v) => { setNewRole(v); setCreateError(null); }} />
          <CreateField label="Department" value={newDepartment} onChange={(v) => { setNewDepartment(v); setCreateError(null); }} />

          {/* Required group selector + inline new-group */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 160 }}>
            <span style={createLabelStyle()}>Group *</span>
            <select
              value={groupId}
              onChange={(e) => { setGroupId(e.target.value); setCreateError(null); }}
              style={{ ...createInputStyle(), appearance: "auto" }}
            >
              <option value="">Select a group…</option>
              {groups.map((g) => (
                <option key={g.group_id} value={g.group_id}>{g.name}</option>
              ))}
              <option value="__new__">+ New group…</option>
            </select>
          </div>

          {creatingNewGroup && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 160 }}>
              <span style={createLabelStyle()}>New group name</span>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  value={newGroupName}
                  onChange={(e) => { setNewGroupName(e.target.value); setCreateError(null); }}
                  placeholder="e.g. Finance leadership"
                  style={createInputStyle()}
                />
                <Button
                  variant="secondary"
                  disabled={!newGroupName.trim() || createGroupMut.isPending}
                  onClick={() => createGroupMut.mutate()}
                  style={{
                    padding: "7px 12px",
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 600,
                    border: "1px solid var(--accent)",
                    background: !newGroupName.trim() ? "var(--bg-tertiary)" : "var(--accent)",
                    color: !newGroupName.trim() ? "var(--text-muted)" : "white",
                    whiteSpace: "nowrap",
                  }}
                >
                  {createGroupMut.isPending ? "…" : "Create group"}
                </Button>
              </div>
            </div>
          )}

          <Button
            variant="primary"
            disabled={!canCreate}
            onClick={() => {
              if (!newName.trim()) return;
              if (!newPhone.trim() && !newEmail.trim()) {
                setCreateError("At least one of phone or email is required.");
                return;
              }
              if (!groupId || creatingNewGroup) {
                setCreateError("Select a group (or create one) before adding the target.");
                return;
              }
              setCreateError(null);
              createTarget.mutate(
                {
                  full_name: newName.trim(),
                  first_name: newFirstName.trim() || null,
                  last_name: newLastName.trim() || null,
                  primary_phone_e164: newPhone.trim() || null,
                  primary_email: newEmail.trim() || null,
                  role: newRole.trim() || null,
                  department: newDepartment.trim() || null,
                  group_id: groupId,
                },
                {
                  onSuccess: () => { setShowCreate(false); resetCreateForm(); },
                  onError: (err) =>
                    setCreateError(err instanceof Error ? err.message : "Failed to create target."),
                },
              );
            }}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--accent)",
              background: canCreate ? "var(--accent)" : "var(--bg-tertiary)",
              color: canCreate ? "white" : "var(--text-muted)",
              whiteSpace: "nowrap",
            }}
          >
            {createTarget.isPending ? "Creating..." : "Create"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => { setShowCreate(false); resetCreateForm(); }}
            style={{
              padding: "7px 12px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-muted)",
              whiteSpace: "nowrap",
            }}
          >
            Cancel
          </Button>
          {createError && (
            <div style={{ width: "100%", fontSize: 11, color: "var(--error)", fontFamily: "var(--font-mono)", letterSpacing: "0.04em" }}>
              {createError}
            </div>
          )}
        </div>
      )}

      {preview != null && (
        <CsvImportBar
          totalRows={preview.total_rows}
          validCount={preview.valid.length}
          duplicateCount={preview.duplicates.length}
          invalidCount={preview.invalid.length}
          loading={commitMutation.isPending}
          error={commitMutation.error instanceof Error ? commitMutation.error.message : null}
          onCommit={handleCommit}
          onDiscard={() => setPreview(null)}
        />
      )}

      <TargetsList
        items={items}
        selectedId={selected?.target_id ?? null}
        matchedPhone={matchedPhone}
        onSelect={(t) => setSelected(t)}
        onRequestDelete={(t) => setConfirmTarget(t)}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
      />

      <Sheet
        open={selected != null}
        onClose={() => setSelected(null)}
        title={selected?.full_name ?? "Target"}
      >
        {selected != null && (
          <TargetDrawer
            target={selected}
            onClose={() => setSelected(null)}
            onRequestDelete={() => setConfirmTarget(selected)}
            onSave={(body) => {
              updateTarget.mutate(body, {
                onSuccess: () => setSelected(null),
                onError: (err) =>
                  alert(err instanceof Error ? err.message : "Failed to update target."),
              });
            }}
            renderFooter={({ onCancel, onSave }) => (
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={onCancel} style={sheetCancelBtn()}>cancel</button>
                <button onClick={onSave} style={sheetSaveBtn()}>save</button>
              </div>
            )}
          />
        )}
      </Sheet>

      <ConfirmDialog
        open={confirmTarget != null}
        title={`Delete ${confirmTarget?.full_name ?? "target"}?`}
        body={
          <>
            {(confirmTarget?.group_names?.length ?? 0) > 0
              ? `Removes it from ${confirmTarget?.group_names?.length} group(s) and `
              : ""}
            permanently deletes the target record. This can&apos;t be undone.
          </>
        }
        busy={deleteTarget.isPending}
        onConfirm={confirmSingleDelete}
        onCancel={() => setConfirmTarget(null)}
      />

      <ConfirmDialog
        open={confirmBulk}
        title={`Delete ${selectedIds.size} target(s)?`}
        body={
          <>
            Permanently deletes {selectedIds.size} target record(s) and removes
            their group memberships. This can&apos;t be undone.
            {selectedTestCount > 0 && (
              <div style={{ marginTop: 6, color: "var(--text-muted)" }}>
                {selectedTestCount} of these are test-pool numbers.
              </div>
            )}
          </>
        }
        busy={deleteTarget.isPending}
        onConfirm={() => void confirmBulkDelete()}
        onCancel={() => setConfirmBulk(false)}
      />
    </div>
  );
}

// ─── Create-form field ───────────────────────────────────────────────────────────

function CreateField({
  label,
  value,
  onChange,
  mono,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 140 }}>
      <span style={createLabelStyle()}>{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        style={createInputStyle(mono)}
      />
    </div>
  );
}

function createLabelStyle(): React.CSSProperties {
  return {
    fontSize: 9.5,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--text-muted)",
  };
}

// ─── Sheet footer buttons ──────────────────────────────────────────────────────

function sheetCancelBtn(): React.CSSProperties {
  return {
    padding: "8px 14px",
    borderRadius: 6,
    background: "var(--bg-tertiary)",
    border: "1px solid var(--border)",
    color: "var(--text-secondary)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    minHeight: "var(--tap-min)",
  };
}

function sheetSaveBtn(): React.CSSProperties {
  return {
    padding: "8px 16px",
    borderRadius: 6,
    background: "var(--accent)",
    border: "1px solid var(--accent)",
    color: "white",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    minHeight: "var(--tap-min)",
  };
}

// ─── Create-form input style ─────────────────────────────────────────────────────

function createInputStyle(mono = false): React.CSSProperties {
  return {
    background: "var(--bg-input)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "7px 10px",
    color: "var(--text-primary)",
    fontSize: 12,
    fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };
}

// ─── CSV export ─────────────────────────────────────────────────────────────────

function csvEscape(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function exportCsv(items: TargetListItem[]): void {
  const header = "name,phone,email,title,org\n";
  const rows = items
    .map((t) =>
      [
        t.full_name,
        t.primary_phone_e164 ?? "",
        t.primary_email ?? "",
        t.role ?? "",
        t.department ?? "",
      ]
        .map(csvEscape)
        .join(","),
    )
    .join("\n");

  const blob = new Blob([header + rows], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "anglerfish-targets.csv";
  anchor.click();
  URL.revokeObjectURL(url);
}
